import Button from "@components/Button"
import Dialog from "@components/Dialog"
import StatusIcon from "@components/StatusIcon"
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { RefundType } from "@pages/helpCenter/RefundPage"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { RevenueCatSubscription } from "src/types"

interface Props {
  isSubmitting: RefundType | null
  onClose: () => void
  open: boolean
  refundSubscriptions: (refundType: RefundType) => Promise<void>
  refundableSubscriptions: RevenueCatSubscription[]
  setIsSubmitting: (isSubmitting: RefundType | null) => void
}

export default function RefundMultipleSubscriptionsDialog({
  isSubmitting,
  onClose,
  open,
  refundSubscriptions,
  refundableSubscriptions,
  setIsSubmitting,
}: Props) {
  const { t } = useTranslation()

  const handleRefundAllSubscriptions = async () => {
    setIsSubmitting("all")
    await refundSubscriptions("all")
    setIsSubmitting(null)
  }

  const handleRefundWebSubscriptions = async () => {
    setIsSubmitting("web")
    await refundSubscriptions("web")
    setIsSubmitting(null)
  }

  const hasAppleSubscription = refundableSubscriptions.some((subscription) =>
    ["app_store", "mac_app_store"].includes(subscription.store),
  )

  const hasGoogleSubscription = refundableSubscriptions.some(
    (subscription) => subscription.store === "play_store",
  )

  return (
    <Dialog onClose={onClose} open={open}>
      <div className="flex h-full flex-col items-center justify-center gap-5 text-center">
        <StatusIcon icon={ExclamationTriangleIcon} tone="yellow" />

        <div className="w-full space-y-3">
          <p className="vm-heading">
            {t("pages.helpCenter.refund.multipleActiveSubscriptions")}
          </p>

          <p className="vm-subheading pb-2">
            {t("pages.helpCenter.refund.whichOneToRefund")}
          </p>

          <Button
            onClick={handleRefundWebSubscriptions}
            submitting={isSubmitting === "web"}
            title="WEB"
            variant="primary"
          />

          {hasAppleSubscription && (
            <Link
              className="vm-button-secondary"
              to="/help-center/refund/apple"
            >
              Apple
            </Link>
          )}

          {hasGoogleSubscription && (
            <Link
              className="vm-button-secondary"
              to="/help-center/refund/google"
            >
              Google
            </Link>
          )}

          <Button
            onClick={handleRefundAllSubscriptions}
            submitting={isSubmitting === "all"}
            title={t("pages.helpCenter.cancelation.all")}
            variant="secondary"
          />
        </div>
      </div>
    </Dialog>
  )
}
